/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 * Migrated from: lego/page/browser/list/PropertySearchPageMenu.jsx
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import ActionsMenuBig from '../../components/shared/ActionsMenuBig'
import SaveAndShareReportModal from '../../components/reports/SaveAndShareReportModal'
import { SaveFilterType } from '../../constants/reportTypes'
import usePropertySearchService from '../../hooks/usePropertySearchService'

const MAX_FILTER_NAME_LENGTH = 45
const TRUNCATED_FILTER_NAME_LENGTH = 42

const LOADING_MENU_PLACEHOLDER = {
  value: 'Loading...',
  disabled: true
}

interface PropertySearchPageMenuProps {
  // No props needed as it reads from context/API
}

export const PropertySearchPageMenu: React.FC<PropertySearchPageMenuProps> = () => {
  const navigate = useNavigate()
  const { showModal, hideModal } = useModal()
  const context = useServiceContext()
  const {
    service: {
      auth,
      init,
      filter: { translate },
      toast
    },
    api,
    constant,
    store
  } = context
  const { assembleFiltersFromFormValues } = usePropertySearchService()

  const [items, setItems] = useState([LOADING_MENU_PLACEHOLDER])
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    isEdit: boolean
    item?: any
  }>({
    isOpen: false,
    isEdit: false,
    item: undefined
  })

  const isPowerUser = auth.getRole() === constant.user.roles.POWER_USER
  const isEosEnabled = init.getFeaturesInfo()?.eosFeature?.show || false

  useEffect(() => {
    loadMenuItems()
  }, [])

  const loadMenuItems = async () => {
    const menuItems: any[] = []

    // Only show "Save Properties Search" if NOT (isEosEnabled && isPowerUser)
    if (!(isEosEnabled && isPowerUser)) {
      menuItems.push({
        value: 'SAVED_FILTERS_SAVE_PROPERTIES_SEARCH_H_TITLE',
        action: () => {
          setModalState({
            isOpen: true,
            isEdit: false,
            item: undefined
          })
        }
      })
    }

    try {
      const savedFilters = await api.formFilters.getAllSavedReportsFilters({
        pageSize: 5,
        pageStart: 0,
        sortBy: 'createdAt',
        sortDesc: true,
        filterTypes: SaveFilterType.PROPERTIES_SEARCH
      })

      if (savedFilters?.elements?.length > 0) {
        menuItems.push({ divider: true })
        menuItems.push({
          value: 'MY_SAVED_PROPERTIES_SEARCH_SETTINGS_TITLE',
          disabled: true
        })

        savedFilters.elements.forEach((filter: any) => {
          const displayName =
            filter.name?.length > MAX_FILTER_NAME_LENGTH
              ? filter.name.substring(0, TRUNCATED_FILTER_NAME_LENGTH) + '...'
              : filter.name

          const submenu: any[] = [
            {
              value: 'SAVED_FILTERS_ACTION_RUN_REPORT',
              action: () => {
                // Navigate to property search with filter ID as query parameter
                navigate(`/documents/property-search?propertySearchFilter=${filter.id}`)
              }
            }
          ]

          // Only show Edit and Remove actions if NOT (isEosEnabled && isPowerUser)
          if (!(isEosEnabled && isPowerUser)) {
            if (auth.getUserId() === filter.ownerId) {
              // User is owner - show Edit and Remove
              submenu.push(
                {
                  value: 'SAVED_FILTERS_ACTION_EDIT',
                  action: () => {
                    setModalState({
                      isOpen: true,
                      isEdit: true,
                      item: filter
                    })
                  }
                },
                {
                  value: 'ACTION_REMOVE',
                  action: async () => {
                    try {
                      await api.formFilters.deleteFilter(
                        filter.id,
                        'propertiesSearches',
                        filter,
                        undefined,
                        { showModal, hideModal },
                        toast,
                        translate,
                        loadMenuItems
                      )
                    } catch (error: any) {
                      // Error already handled by service (toast shown)
                      // Only log if not user cancellation
                      if (error?.message !== 'User cancelled') {
                        console.error('Delete filter error:', error)
                      }
                    }
                  }
                }
              )
            } else {
              // User is not owner - show Save As
              submenu.push({
                value: 'SAVED_FILTERS_ACTION_SAVE_AS',
                action: () => {
                  // For "Save As", open the save modal with the filter's data pre-populated
                  // but treat it as a new save (not edit mode)
                  setModalState({
                    isOpen: true,
                    isEdit: false,
                    item: filter // Pass filter to pre-populate fields
                  })
                }
              })
            }
          }

          menuItems.push({
            value: displayName,
            submenu
          })
        })

        // Add "More..." action to navigate to saved property search settings page
        menuItems.push({
          value: 'MORE...',
          action: () => {
            navigate('/MySavedReportSettings?from=savedPropSrchSettings')
          }
        })
      }
    } catch (error) {
      console.error('Failed to load saved property searches:', error)
    }

    setItems(menuItems)
  }

  const handleModalClose = () => {
    setModalState({
      isOpen: false,
      isEdit: false,
      item: undefined
    })
    // Reload menu items after save/edit
    loadMenuItems()
  }

  return (
    <div>
      <ActionsMenuBig menuId="page" items={items} />

      {modalState.isOpen && (
        <SaveAndShareReportModal
          type={SaveFilterType.PROPERTIES_SEARCH}
          filterAssembler={() => assembleFiltersFromFormValues(store?.formRef?.values || {})}
          onClose={handleModalClose}
          isEdit={modalState.isEdit}
          item={modalState.item}
        />
      )}
    </div>
  )
}

export default PropertySearchPageMenu
